import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/admin/ui";
import { ShieldAlert, Loader2 } from "lucide-react";

const PHRASE = { quarantine: "QUARANTINE", purge: "DELETE PERMANENTLY" };

function Row({ r, checked, onToggle }) {
  const locked = r.blocked.length > 0;
  return (
    <tr className="border-t border-[#E2EBF0] align-top" data-testid={`td-row-${r.id}`}>
      <td className="py-3 pr-3">
        <input type="checkbox" disabled={locked} checked={checked} onChange={onToggle}
          data-testid={`td-select-${r.id}`} className="h-4 w-4 accent-[#10B981]" />
      </td>
      <td className="py-3 pr-3 text-xs text-[#4A635A]">{r.type === "client" ? "Client" : "Service request"}</td>
      <td className="py-3 pr-3">
        <div className="text-sm font-medium text-[#0D3B2D]">{r.name}</div>
        <div className="text-xs text-[#4A635A]">{r.email}</div>
        {r.blocked.map((b) => <div key={b} className="text-[11px] text-red-600 flex items-center gap-1 mt-1"><ShieldAlert className="h-3 w-3" />Protected — {b}</div>)}
        {r.warnings.map((w) => <div key={w} className="text-[11px] text-amber-600 mt-1">{w}</div>)}
      </td>
      <td className="py-3 pr-3 text-xs text-[#4A635A]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
      <td className="py-3 pr-3 text-xs text-[#4A635A]">{r.services.map((s) => s.name).filter(Boolean).join(", ") || "—"}</td>
      <td className="py-3 text-xs">
        {r.quarantined ? <span className="text-amber-700">Quarantined</span> : r.likely_test ? <span className="text-[#10B981]">Likely test</span> : <span className="text-[#4A635A]">—</span>}
      </td>
    </tr>
  );
}

function PreviewBox({ preview }) {
  return (
    <div className="mt-4 rounded-lg border border-[#E2EBF0] bg-[#F8FAF9] p-4 text-xs text-[#0D3B2D] space-y-2" data-testid="td-preview">
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A635A]">Read-only preview — nothing changed yet</div>
      {preview.clients.map((c) => (
        <div key={c.id}><b>{c.name}</b> ({c.email}) — {Object.entries(c.related).filter(([, n]) => n).map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`).join(", ") || "no related records"}</div>
      ))}
      {preview.requests.map((r) => <div key={r.id}><b>{r.name}</b> ({r.email}) — service request</div>)}
    </div>
  );
}

export default function TestData() {
  const [data, setData] = useState(null);
  const [onlyLikely, setOnlyLikely] = useState(true);
  const [sel, setSel] = useState({});
  const [preview, setPreview] = useState(null);
  const [action, setAction] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/test-data/candidates").then((r) => setData(r.data)).catch(() => toast.error("Could not load records"));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const all = [...data.clients, ...data.requests];
    return onlyLikely ? all.filter((r) => r.likely_test || r.quarantined) : all;
  }, [data, onlyLikely]);
  const picked = rows.filter((r) => sel[r.id]);
  const body = (extra = {}) => ({
    client_ids: picked.filter((r) => r.type === "client").map((r) => r.id),
    request_ids: picked.filter((r) => r.type === "service_request").map((r) => r.id), ...extra,
  });
  const reset = () => { setSel({}); setPreview(null); setAction(null); setPhrase(""); load(); };

  const doPreview = async (a) => {
    const r = await api.post("/admin/test-data/preview", body());
    setPreview(r.data); setAction(a); setPhrase("");
  };
  const run = async (a) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/test-data/${a}`, body({ confirm: phrase }));
      toast.success(a === "restore" ? `${r.data.restored} restored` : a === "quarantine" ? `${r.data.quarantined} quarantined` : `${r.data.deleted} permanently deleted`);
      reset();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };

  const allQuarantined = picked.length > 0 && picked.every((r) => r.quarantined);
  return (
    <div data-testid="test-data-page">
      <PageHeader title="Test Data Cleanup" subtitle="Review QA records, quarantine them (reversible), and delete permanently only from quarantine. Stripe is never contacted." />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm text-[#0D3B2D] flex items-center gap-2">
          <input type="checkbox" checked={onlyLikely} onChange={(e) => setOnlyLikely(e.target.checked)} data-testid="td-only-likely" className="accent-[#10B981]" />
          Show likely test records only
        </label>
        <span className="text-xs text-[#4A635A]">{picked.length} selected</span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" disabled={!picked.length || allQuarantined} onClick={() => doPreview("quarantine")} data-testid="td-preview-quarantine">Preview quarantine</Button>
          <Button variant="outline" disabled={!allQuarantined} onClick={() => run("restore")} data-testid="td-restore">Restore</Button>
          <Button variant="outline" disabled={!allQuarantined} onClick={() => doPreview("purge")} data-testid="td-preview-purge" className="text-red-600 border-red-200">Preview permanent delete</Button>
        </div>
      </div>
      {preview && action && (
        <div className="mb-5">
          <PreviewBox preview={preview} />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-[#0D3B2D]">Type <b>{PHRASE[action]}</b> to confirm:</span>
            <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} className="h-9 max-w-[240px]" data-testid="td-confirm-input" />
            <Button disabled={phrase !== PHRASE[action] || busy} onClick={() => run(action)} data-testid="td-confirm-button"
              className={action === "purge" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#0D3B2D] text-white"}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : action === "purge" ? "Delete permanently" : "Quarantine"}
            </Button>
            <Button variant="ghost" onClick={() => { setPreview(null); setAction(null); }} data-testid="td-cancel">Cancel</Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-[#E2EBF0] rounded-xl p-4 overflow-x-auto">
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-[#10B981]" /> : (
          <table className="w-full text-left min-w-[760px]">
            <thead><tr className="text-[10px] uppercase tracking-widest text-[#4A635A]"><th /><th>Type</th><th>Name / Email</th><th>Date</th><th>Services</th><th>Status</th></tr></thead>
            <tbody>{rows.map((r) => <Row key={r.id} r={r} checked={!!sel[r.id]} onToggle={() => { setPreview(null); setSel((s) => ({ ...s, [r.id]: !s[r.id] })); }} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
